import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { HardHat, Briefcase } from "lucide-react";

/**
 * WorkPeopleDialog — toggles visibility of employees and subcontractors
 * in the work tracking grid (per-user preference).
 */
const WorkPeopleDialog = ({
  open,
  onOpenChange,
  showInactive,
  toggleShowInactive,
  hiddenWorkerIds,
  toggleWorkerVisibility,
  resetVisibility,
  employees,
  allEmployees,
  subcontractors,
}) => {
  const empPool = showInactive ? allEmployees : employees;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh]" data-testid="people-visibility-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Personnes affichées</DialogTitle>
          <DialogDescription>
            Décoche pour masquer un salarié ou un sous-traitant du suivi des chantiers. Les préférences ne concernent que toi.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between border rounded-xl p-2 mb-2">
          <Label className="text-sm cursor-pointer flex items-center gap-2" onClick={toggleShowInactive}>
            <Checkbox checked={showInactive} onCheckedChange={toggleShowInactive} data-testid="toggle-show-inactive" />
            Afficher les salariés inactifs (sortis)
          </Label>
        </div>
        <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
          <div>
            <div className="text-[10px] uppercase tracking-wide font-semibold text-blue-800 mb-1 flex items-center gap-1">
              <HardHat className="h-3 w-3" /> Salariés
            </div>
            <div className="space-y-1">
              {empPool.map(emp => {
                const k = `employee:${emp.id}`;
                const visible = !hiddenWorkerIds[k];
                return (
                  <label key={emp.id} className="flex items-center gap-2 text-sm p-1 rounded hover:bg-zinc-100 cursor-pointer transition-colors">
                    <Checkbox
                      checked={visible}
                      onCheckedChange={() => toggleWorkerVisibility("employee", emp.id)}
                      data-testid={`toggle-employee-${emp.id}`}
                    />
                    <span className={visible ? "" : "text-muted-foreground line-through"}>
                      {emp.first_name} {emp.last_name}
                      {emp.exit_date && <span className="ml-1 text-[10px] text-muted-foreground">(sorti)</span>}
                    </span>
                  </label>
                );
              })}
              {empPool.length === 0 && (
                <p className="text-xs text-muted-foreground italic">Aucun salarié</p>
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide font-semibold text-purple-800 mb-1 flex items-center gap-1">
              <Briefcase className="h-3 w-3" /> Sous-traitants
            </div>
            <div className="space-y-1">
              {subcontractors.map(sub => {
                const k = `subcontractor:${sub.id}`;
                const visible = !hiddenWorkerIds[k];
                const name = sub.company_name || `${sub.first_name || ""} ${sub.last_name || ""}`.trim() || "Sous-traitant";
                return (
                  <label key={sub.id} className="flex items-center gap-2 text-sm p-1 rounded hover:bg-zinc-100 cursor-pointer transition-colors">
                    <Checkbox
                      checked={visible}
                      onCheckedChange={() => toggleWorkerVisibility("subcontractor", sub.id)}
                      data-testid={`toggle-subcontractor-${sub.id}`}
                    />
                    <span className={visible ? "" : "text-muted-foreground line-through"}>{name}</span>
                  </label>
                );
              })}
              {subcontractors.length === 0 && (
                <p className="text-xs text-muted-foreground italic">Aucun sous-traitant</p>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={resetVisibility} data-testid="people-reset">Tout afficher</Button>
          <Button onClick={() => onOpenChange(false)} data-testid="people-close">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkPeopleDialog;
